import React, { useState } from 'react';
import { connect } from 'react-redux';
import { post } from '../../util/api';
import earnMoneyImg from '../../assets/earn-money.png';

import { LayoutSingleColumn, NamedLink, NamedRedirect, Page } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import { isScrollingDisabled } from '../../ducks/ui.duck';
import { fetchCurrentUser } from '../../ducks/user.duck';
import { getIsAffiliate } from '../../util/userTypes';

import css from './BecomeAffiliatePage.module.css';

const affiliateTypeOptions = [
  {
    id: 'pet-charity',
    title: 'Pet Charity',
    description: 'Commission starts at 50% per registration. Registered charity number required.',
  },
  {
    id: 'website-owner',
    title: 'Website Owner',
    description: 'Commission starts at 10% and rises by 10% for every 10 new joiners.',
  },
  {
    id: 'social-creator',
    title: 'Social Creator',
    description: 'Commission starts at 10% and rises by 10% for every 10 new joiners.',
  },
];

const websiteSocialRates = [
  ['0 - 9', '10%'],
  ['10 - 19', '20%'],
  ['20 - 29', '30%'],
  ['30 - 39', '40%'],
  ['40+', '50%'],
];

const creatorTools = [
  'Ready-made banners, buttons, and visuals with embedded affiliate links.',
  'Personalised QR codes for Instagram, TikTok, YouTube, Facebook, and offline events.',
  'Automatic click and registration attribution with no technical setup required.',
];

const charityBenefits = [
  'Enhanced commission rates from the first referral.',
  'A sustainable revenue stream for rescue and rehoming work.',
  'A partnership aligned with ethical pet care and community-led travel.',
];

const reasonsToJoin = [
  'High earning potential with commission rates up to 50%.',
  'Transparent tracking via unique links and QR codes.',
  'Creative assets provided, with no design work needed.',
  'Extra support for pet charities through elevated commission tiers.',
  'A mission-driven platform built around trust, community, and collaboration.',
];

export const BecomeAffiliatePageComponent = props => {
  const { currentUser, scrollingDisabled, onRefreshCurrentUser } = props;

  const isAffiliate = getIsAffiliate(currentUser);
  const affiliateStatus = currentUser?.attributes?.profile?.protectedData?.affiliateStatus;

  if (isAffiliate) {
    return <NamedRedirect name="AffiliateAreaPage" />;
  }

  const firstName = currentUser?.attributes?.profile?.firstName || '';
  const lastName = currentUser?.attributes?.profile?.lastName || '';
  const email = currentUser?.attributes?.email || '';

  const [formValues, setFormValues] = useState({
    name: firstName && lastName ? `${firstName} ${lastName}` : '',
    email,
    affiliateType: 'website-owner',
    charityNumber: '',
    website: '',
    reason: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const isPetCharity = formValues.affiliateType === 'pet-charity';

  const handleChange = e => {
    const { name, value } = e.target;
    setFormValues(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    if (isPetCharity && !formValues.charityNumber.trim()) {
      setError('Registered charity number is required for pet charity applications.');
      setSubmitting(false);
      return;
    }

    try {
      // Same contract as AffiliateApplyPage: Empavision persists the group as
      // `affiliateGroup` (canonical slugs pet-charity | website-owner |
      // social-creator) and tenant-specific answers ride in `applicationData`.
      await post('/api/affiliate/apply', {
        affiliateGroup: formValues.affiliateType,
        applicationData: isPetCharity
          ? { charityNumber: formValues.charityNumber.trim() }
          : null,
        websiteUrl: formValues.website || null,
        applicationReason: formValues.reason || null,
      });
      setSubmitted(true);
      // Refresh currentUser so affiliateStatus:'pending' is in Redux — prevents
      // re-applying if the user navigates away and back to this page.
      if (onRefreshCurrentUser) onRefreshCurrentUser();
    } catch (err) {
      // 409 means already applied (pending or approved) — treat as submitted
      // and sync Redux so the pending message shows on future page visits.
      if (err && err.error && err.error.includes('already applied')) {
        setSubmitted(true);
        if (onRefreshCurrentUser) onRefreshCurrentUser();
      } else {
        setError('Something went wrong. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const renderApplicationSection = () => {
    if (!currentUser) {
      return (
        <div className={css.loginPrompt}>
          <p>You need to be logged in to apply for the affiliate programme.</p>
          <NamedLink name="LoginPage" className={css.loginLink}>
            Log in or create an account
          </NamedLink>
        </div>
      );
    }

    if (submitted) {
      return (
        <div className={css.successMessage}>
          <p>Your application has been submitted. We'll review it and email you within 2 business days.</p>
        </div>
      );
    }

    if (affiliateStatus === 'pending') {
      return (
        <div className={css.pendingMessage}>
          <p>Your application is under review. We'll email you when it's approved.</p>
        </div>
      );
    }

    return (
      <form className={css.form} onSubmit={handleSubmit}>
        <div className={css.fieldset}>
          <span className={css.legend}>Affiliate type</span>
          <div className={css.optionGrid}>
            {affiliateTypeOptions.map(option => {
              const checked = formValues.affiliateType === option.id;
              return (
                <label key={option.id} className={checked ? css.optionCardSelected : css.optionCard}>
                  <input
                    className={css.optionInput}
                    type="radio"
                    name="affiliateType"
                    value={option.id}
                    checked={checked}
                    onChange={handleChange}
                  />
                  <span className={css.optionTitle}>{option.title}</span>
                  <span className={css.optionDescription}>{option.description}</span>
                </label>
              );
            })}
          </div>
        </div>

        <div className={css.fieldRow}>
          <div className={css.field}>
            <label htmlFor="name" className={css.label}>Name <span className={css.required}>*</span></label>
            <input
              id="name"
              name="name"
              type="text"
              className={css.input}
              value={formValues.name}
              onChange={handleChange}
              required
            />
          </div>

          <div className={css.field}>
            <label htmlFor="email" className={css.label}>Email <span className={css.required}>*</span></label>
            <input
              id="email"
              name="email"
              type="email"
              className={css.input}
              value={formValues.email}
              onChange={handleChange}
              required
            />
          </div>
        </div>

        {isPetCharity ? (
          <div className={css.field}>
            <label htmlFor="charityNumber" className={css.label}>Registered Charity Number <span className={css.required}>*</span></label>
            <input
              id="charityNumber"
              name="charityNumber"
              type="text"
              className={css.input}
              value={formValues.charityNumber}
              onChange={handleChange}
              required
            />
          </div>
        ) : null}

        <div className={css.field}>
          <label htmlFor="website" className={css.label}>Website or Social Profile <span className={css.optional}>(optional)</span></label>
          <input
            id="website"
            name="website"
            type="url"
            className={css.input}
            value={formValues.website}
            onChange={handleChange}
            placeholder="https://yourwebsite.com"
          />
        </div>

        <div className={css.field}>
          <label htmlFor="reason" className={css.label}>Why do you want to join? <span className={css.optional}>(optional)</span></label>
          <textarea
            id="reason"
            name="reason"
            className={css.textarea}
            value={formValues.reason}
            onChange={handleChange}
            rows={4}
          />
        </div>

        {error ? <p className={css.errorMessage}>{error}</p> : null}
        <button type="submit" className={css.submitButton} disabled={submitting}>
          {submitting ? 'Submitting...' : 'Join as an Affiliate'}
        </button>
      </form>
    );
  };

  return (
    <Page title="HouseSit Match Affiliate Programme" scrollingDisabled={scrollingDisabled}>
      <LayoutSingleColumn topbar={<TopbarContainer />} footer={<FooterContainer />}>
        <div className={css.root}>
          <section className={css.heroSection}>
            <div className={css.heroCopy}>
              <p className={css.kicker}>Grow With Us, Earn With Us</p>
              <h1 className={css.heroTitle}>Join the HouseSit Match Affiliate Programme</h1>
              <p className={css.heroDescription}>
                At HouseSit Match, community has always been at the heart of what we do. Our affiliate programme is designed to reward the people and organisations who help our network grow, from travel bloggers and social creators to pet charities and rescue groups.
              </p>
              <a href="#affiliate-signup" className={css.heroButton}>Join as an Affiliate</a>
              <p className={css.reviewNote}>No technical setup. No complicated dashboards. Just clear, trackable rewards.</p>
            </div>
          </section>

          <section className={css.contentSection}>
            <div className={css.sectionHeader}>
              <p className={css.sectionEyebrow}>Commission Structure</p>
              <h2 className={css.sectionTitle}>Earn up to 50% commission with a transparent tiered model</h2>
            </div>
            <div className={css.rateGrid}>
              <article className={css.rateCard}>
                <h3 className={css.cardTitle}>Website Owners & Social Creators</h3>
                <table className={css.table}>
                  <thead>
                    <tr>
                      <th>Referrals made</th>
                      <th>Commission rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {websiteSocialRates.map(([range, rate]) => (
                      <tr key={range}>
                        <td>{range}</td>
                        <td>{rate}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </article>
              <article className={css.rateCardAccent}>
                <div className={css.petCardInner}>
                  <div className={css.petCardText}>
                    <h3 className={css.cardTitle}>Pet Charities</h3>
                    <p className={css.bigRate}>50%</p>
                    <p className={css.cardText}>Flat commission per registration to support rescue, rehoming, and sustainable fundraising.</p>
                  </div>
                  <div className={css.petCardImageWrap}>
                    <img
                      src={earnMoneyImg}
                      alt="Earning money illustration - hand dropping coins into piggy bank"
                      className={css.petCardImage}
                    />
                  </div>
                </div>
              </article>
            </div>
          </section>

          <section className={css.contentSection}>
            <div className={css.sectionHeader}>
              <p className={css.sectionEyebrow}>Promotional Tools</p>
              <h2 className={css.sectionTitle}>Simple tools to help you promote HouseSit Match</h2>
            </div>
            <div className={css.twoColumnGrid}>
              <article className={css.infoCard}>
                <h3 className={css.cardTitle}>For Websites & Blogs</h3>
                <ul className={css.bulletList}>
                  <li>Ready-made creative assets including banners, buttons, and promotional visuals.</li>
                  <li>Unique affiliate links embedded into each creative automatically.</li>
                  <li>Automatic click and registration attribution without extra setup.</li>
                </ul>
              </article>
              <article className={css.infoCard}>
                <h3 className={css.cardTitle}>For Social Media Creators</h3>
                <ul className={css.bulletList}>
                  {creatorTools.map(item => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </article>
            </div>
          </section>

          <section className={css.contentSectionAlt}>
            <div className={css.sectionHeader}>
              <p className={css.sectionEyebrow}>Pet Charities</p>
              <h2 className={css.sectionTitle}>Earn a special high rate while supporting animal welfare</h2>
            </div>
            <p className={css.sectionText}>
              HouseSit Match already supports animal welfare through rescue and rehoming work. This programme extends that mission by giving charities a meaningful revenue stream tied to ethical pet care and trusted community travel.
            </p>
            <ul className={css.bulletListWide}>
              {charityBenefits.map(item => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </section>

          <section className={css.contentSection}>
            <div className={css.sectionHeader}>
              <p className={css.sectionEyebrow}>Why Join?</p>
              <h2 className={css.sectionTitle}>Why join the HouseSit Match Affiliate Programme?</h2>
            </div>
            <div className={css.reasonGrid}>
              {reasonsToJoin.map(reason => (
                <article key={reason} className={css.reasonCard}>
                  <p>{reason}</p>
                </article>
              ))}
            </div>
            <p className={css.sectionText}>
              Whether you are a content creator, community leader, pet-care advocate, or registered charity, the programme is designed to help you promote HouseSit Match with a clear path to earning.
            </p>
          </section>

          <section id="affiliate-signup" className={css.applicationSection}>
            <div className={css.sectionHeader}>
              <p className={css.sectionEyebrow}>Application</p>
              <h2 className={css.sectionTitle}>Apply to join the affiliate programme</h2>
            </div>
            {renderApplicationSection()}
          </section>
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

const mapStateToProps = state => {
  const { currentUser } = state.user;
  return {
    currentUser,
    scrollingDisabled: isScrollingDisabled(state),
  };
};

const mapDispatchToProps = dispatch => ({
  onRefreshCurrentUser: () => dispatch(fetchCurrentUser()),
});

const BecomeAffiliatePage = connect(mapStateToProps, mapDispatchToProps)(BecomeAffiliatePageComponent);

export default BecomeAffiliatePage;
